import { EditOutlined, KeyOutlined, PlusOutlined, SafetyOutlined } from "@ant-design/icons";
import { addPreset, presetsHeld, removePreset, ROLE_AREAS, ROLE_PRESETS, rolesAsBundles, type AssignableRole, type Classification, type PlatformUser, type RoleArea, type UserLocale } from "@ap/api";
import { StatusTag, useAuth, useHasRole, type SessionUser } from "@ap/ui-kit";
import { Alert, App, Button, Checkbox, Collapse, Form, Input, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, useResource } from "../../lib/api";
import { FormIssues, useMutation } from "../../lib/crud";
import { Loadable } from "../../lib/Loadable";

export const PLATFORM_TENANT = "_platform";

/** An organisation by name; the platform's own is "Platform team". */
export function tenantLabel(t: (k: string) => string, tenant: string): string {
  return tenant === PLATFORM_TENANT ? t("platformTeam") : tenant;
}

/** A role in plain words ("Release approver"), never its technical name. */
export { roleLabel } from "../../lib/roleLabel";
import { roleLabel } from "../../lib/roleLabel";
const CLASSIFICATIONS: Classification[] = ["public", "internal", "confidential", "restricted"];
const LOCALES: UserLocale[] = ["ar", "en"];
const USERNAME = /^[a-z0-9][a-z0-9._-]{2,62}$/;
const EMAIL = /^[^@\s]{1,64}@[^@\s]{1,190}\.[^@\s]{2,63}$/;
const PAGE = 20;

/** Assignable roles grouped by console area, in navigation order. */
export function groupRoles(roles: readonly AssignableRole[]): [RoleArea, AssignableRole[]][] {
  return ROLE_AREAS.map((a) => [a, roles.filter((r) => r.area === a)] as [RoleArea, AssignableRole[]]).filter(([, rs]) => rs.length > 0);
}

/** Mirrors the service: platform roles need a platform admin and a platform-tenant user. */
export function roleLocked(role: AssignableRole, platformAdmin: boolean, tenant: string): boolean {
  return role.platform_only && (!platformAdmin || tenant !== PLATFORM_TENANT);
}

/** The signed-in administrator (Keycloak `sub` is the user id). */
export function isSelf(me: Pick<SessionUser, "sub" | "username"> | null, u: Pick<PlatformUser, "id" | "username">): boolean {
  return !!me && (me.sub === u.id || me.username === u.username);
}

export interface UserForm {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

/** Client-side checks (i18n keys `admin:issues.<code>`); the identity service validates again. */
export function userFormIssues(f: UserForm, creating: boolean): string[] {
  const issues: string[] = [];
  if (creating && !USERNAME.test(f.username.trim().toLowerCase())) issues.push("username_invalid");
  if (!f.first_name.trim() || !f.last_name.trim()) issues.push("name_required");
  if (f.email.trim() && !EMAIL.test(f.email.trim())) issues.push("email_invalid");
  return issues;
}

const displayName = (u: Pick<PlatformUser, "first_name" | "last_name" | "username">) => [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username;

/** Users & roles (identity service `/v1/users`, ADR-0053): own tenant, or any for platform admins. */
export function UsersTab() {
  const { t } = useTranslation("admin");
  const tp = useTranslation("pickers").t;
  const { user: me } = useAuth();
  const platform = useHasRole({ anyOf: ["platform-admin"] });
  const [tenant, setTenant] = useState(me?.tenant ?? "");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Switched-off accounts (e.g. old test users) are out of the way unless asked for.
  const [showDisabled, setShowDisabled] = useState(false);
  const tenants = useResource((a) => (platform ? a.controlPlane.tenants() : Promise.resolve([{ tenant: me?.tenant ?? "" }])), [platform, me?.tenant]);
  const roles = useResource((a) => a.users.roles());
  const users = useResource((a) => a.users.list({ search, first: (page - 1) * PAGE, max: PAGE, tenant: platform ? tenant : undefined }), [search, page, tenant, platform]);
  // `?new=1` (command palette "Create user") opens the form directly.
  const [creating, setCreating] = useState(() => new URLSearchParams(location.search).get("new") === "1");
  const [editing, setEditing] = useState<PlatformUser | null>(null);
  const [roleEdit, setRoleEdit] = useState<PlatformUser | null>(null);
  const [resetting, setResetting] = useState<PlatformUser | null>(null);
  const [password, setPassword] = useState<{ name: string; value: string } | null>(null);
  const assignable = new Set((roles.data ?? []).map((r) => r.name));
  const tenantOptions = [...new Set([...(tenants.data ?? []).map((x) => x.tenant), ...(platform ? [PLATFORM_TENANT] : []), tenant].filter(Boolean))];

  return (
    <Space direction="vertical" style={{ inlineSize: "100%" }} size="middle">
      <Alert type="info" showIcon message={t("users.intro")} />
      <Space wrap align="end">
        {platform ? (
          <Form layout="vertical">
            <Form.Item label={t("users.tenant")} htmlFor="users-tenant" style={{ marginBlockEnd: 0 }}>
              <Select
                id="users-tenant"
                style={{ minInlineSize: 200 }}
                value={tenant}
                onChange={(v: string) => {
                  setTenant(v);
                  setPage(1);
                }}
                options={tenantOptions.map((x) => ({ value: x, label: tenantLabel(t, x) }))}
              />
            </Form.Item>
          </Form>
        ) : null}
        <Input.Search
          id="users-search"
          aria-label={t("users.search")}
          placeholder={t("users.search")}
          allowClear
          style={{ inlineSize: 320 }}
          onSearch={(v) => {
            setSearch(v.trim());
            setPage(1);
          }}
        />
        <Space size={6}>
          <Switch size="small" id="users-show-disabled" checked={showDisabled} onChange={setShowDisabled} />
          <label htmlFor="users-show-disabled">{t("users.showDisabled")}</label>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          {t("users.newUser")}
        </Button>
      </Space>
      <Loadable r={users}>
        {(p) => (
          <Table<PlatformUser>
            rowKey="id"
            size="small"
            dataSource={showDisabled ? p.items : p.items.filter((u) => u.enabled)}
            locale={{ emptyText: t("users.none") }}
            scroll={{ x: true }}
            data-testid="users-table"
            pagination={{ current: page, pageSize: PAGE, total: p.total, onChange: setPage, showSizeChanger: false, showTotal: (n) => t("users.total", { n }) }}
            columns={[
              {
                title: t("users.user"),
                key: "u",
                render: (_, u) => (
                  <Space direction="vertical" size={0}>
                    <span dir="auto">
                      {displayName(u)} {isSelf(me, u) ? <Tag>{t("users.you")}</Tag> : null}
                    </span>
                    <code className="ap-mono" dir="ltr">
                      {u.username}
                    </code>
                  </Space>
                ),
              },
              { title: t("users.email"), key: "e", render: (_, u) => (u.email ? <span dir="ltr">{u.email}</span> : "—") },
              ...(platform ? [{ title: t("users.tenant"), key: "t", render: (_: unknown, u: PlatformUser) => <bdi>{tenantLabel(t, u.tenant)}</bdi> }] : []),
              {
                title: t("users.status"),
                key: "s",
                render: (_, u) => <StatusTag status={u.enabled ? "active" : "disabled"} tone={u.enabled ? "success" : "neutral"} label={t(u.enabled ? "users.enabled" : "users.disabled")} />,
              },
              {
                title: t("users.roles"),
                key: "r",
                render: (_, u) => {
                  const shown = u.roles.filter((r) => assignable.has(r));
                  const { bundles, single } = rolesAsBundles(shown);
                  return shown.length ? (
                    <Space size={[4, 4]} wrap style={{ maxInlineSize: 420 }} data-testid={`user-bundles-${u.username}`}>
                      {bundles.map((b) => (
                        <Tag key={b} color="blue" style={{ marginInlineEnd: 0 }}>
                          {tp(`preset.${b}.name`)}
                        </Tag>
                      ))}
                      {single.length ? (
                        <Tooltip title={single.map((r) => roleLabel(t, r)).join(", ")}>
                          <Tag style={{ marginInlineEnd: 0 }}>{bundles.length ? tp("preset.single", { count: single.length }) : single.map((r) => roleLabel(t, r)).join(", ")}</Tag>
                        </Tooltip>
                      ) : null}
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">{t("users.noRoles")}</Typography.Text>
                  );
                },
              },
              { title: t("users.clearance"), key: "c", render: (_, u) => t(`classification.${u.clearance}`) },
              {
                title: t("users.actions"),
                key: "x",
                render: (_, u) => {
                  const self = isSelf(me, u);
                  const name = displayName(u);
                  return (
                    <Space>
                      <Button size="small" icon={<EditOutlined />} aria-label={t("users.edit", { name })} title={t("users.edit", { name })} onClick={() => setEditing(u)} />
                      <Tooltip title={self ? t("users.selfRoles") : t("users.editRoles", { name })}>
                        <Button size="small" icon={<SafetyOutlined />} aria-label={t("users.editRoles", { name })} disabled={self || !roles.data} onClick={() => setRoleEdit(u)} />
                      </Tooltip>
                      <Tooltip title={self ? t("users.selfReset") : t("users.resetPassword", { name })}>
                        <Button size="small" icon={<KeyOutlined />} aria-label={t("users.resetPassword", { name })} disabled={self} onClick={() => setResetting(u)} />
                      </Tooltip>
                    </Space>
                  );
                },
              },
            ]}
          />
        )}
      </Loadable>
      {creating && roles.data ? (
        <CreateModal
          tenant={platform ? tenant : (me?.tenant ?? "")}
          platformAdmin={platform}
          roles={roles.data}
          onClose={() => setCreating(false)}
          onDone={(u) => {
            setCreating(false);
            users.reload();
            if (u.temporary_password) setPassword({ name: u.username, value: u.temporary_password });
          }}
        />
      ) : null}
      {editing ? (
        <EditModal
          user={editing}
          self={isSelf(me, editing)}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            users.reload();
          }}
        />
      ) : null}
      {roleEdit && roles.data ? (
        <RolesModal
          user={roleEdit}
          roles={roles.data}
          platformAdmin={platform}
          onClose={() => setRoleEdit(null)}
          onDone={() => {
            setRoleEdit(null);
            users.reload();
          }}
        />
      ) : null}
      {resetting ? (
        <ResetModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={(value) => {
            setPassword({ name: resetting.username, value });
            setResetting(null);
          }}
        />
      ) : null}
      {password ? <PasswordModal name={password.name} value={password.value} onClose={() => setPassword(null)} /> : null}
    </Space>
  );
}

/**
 * Jobs first (ADR-0065): "Case worker", "Approver", "Agent builder", "Team admin" … tick the
 * roles that job needs; a plain "can do" summary follows the selection. The role checkboxes
 * below stay the precise control.
 */
function JobPresets({ roles, value, onChange, platformAdmin, tenant }: { roles: AssignableRole[]; value: string[]; onChange: (v: string[]) => void; platformAdmin: boolean; tenant: string }) {
  const { t } = useTranslation("pickers");
  const { t: ta } = useTranslation("admin");
  const byName = new Map(roles.map((r) => [r.name, r]));
  const assignable = (name: string) => {
    const r = byName.get(name);
    return !!r && !roleLocked(r, platformAdmin, tenant);
  };
  const offered = ROLE_PRESETS.filter((p) => p.roles.every(assignable));
  const held = new Set(presetsHeld(value, offered));
  return (
    <section aria-labelledby="jobs-title" data-testid="job-presets" style={{ marginBlockEnd: 12 }}>
      <Typography.Title level={3} id="jobs-title" style={{ fontSize: 14 }}>
        {t("preset.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t("preset.help")}
      </Typography.Paragraph>
      <Space wrap>
        {offered.map((p) => (
          <Tooltip key={p.key} title={t(`preset.${p.key}.can`)}>
            <Checkbox checked={held.has(p.key)} onChange={(e) => onChange(e.target.checked ? addPreset(value, p, assignable) : removePreset(value, p, offered))} data-preset={p.key}>
              {t(`preset.${p.key}.name`)}
            </Checkbox>
          </Tooltip>
        ))}
      </Space>
      <div style={{ marginBlockStart: 8 }} data-testid="can-do">
        {value.length ? (
          <>
            <Typography.Text strong>{t("preset.canDo")}</Typography.Text>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {value
                .filter((r) => byName.has(r))
                .map((r) => (
                  <li key={r}>{ta(`users.roleDesc.${r}`, { defaultValue: byName.get(r)?.description ?? r })}</li>
                ))}
            </ul>
          </>
        ) : (
          <Typography.Text type="secondary">{t("preset.nothing")}</Typography.Text>
        )}
      </div>
    </section>
  );
}

/** Checkbox groups by area; locked roles stay visible but cannot be toggled. */
function RoleEditor({ roles, value, onChange, platformAdmin, tenant }: { roles: AssignableRole[]; value: string[]; onChange: (v: string[]) => void; platformAdmin: boolean; tenant: string }) {
  const { t } = useTranslation("admin");
  const selected = new Set(value);
  const anyLocked = roles.some((r) => roleLocked(r, platformAdmin, tenant));
  return (
    <div data-testid="role-editor">
      <JobPresets roles={roles} value={value} onChange={onChange} platformAdmin={platformAdmin} tenant={tenant} />
      <Collapse
        size="small"
        defaultActiveKey={rolesAsBundles(value).single.length ? ["single"] : []}
        items={[
          {
            key: "single",
            label: t("preset.advanced", { ns: "pickers" }),
            children: (
              <div data-testid="single-roles">
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  {t("preset.advancedHelp", { ns: "pickers" })}
                </Typography.Paragraph>
                {anyLocked ? <Typography.Paragraph type="secondary">{t("users.platformOnly")}</Typography.Paragraph> : null}
      {groupRoles(roles).map(([area, list]) => (
        <fieldset key={area} style={{ border: 0, padding: 0, marginInline: 0, marginBlock: "0 12px" }} data-area={area}>
          <legend style={{ fontWeight: 600, marginBlockEnd: 4, fontSize: 14 }}>{t(`users.area.${area}`)}</legend>
          <Space direction="vertical" size={2}>
            {list.map((r) => (
              <Checkbox
                key={r.name}
                checked={selected.has(r.name)}
                disabled={roleLocked(r, platformAdmin, tenant)}
                onChange={(e) => onChange(e.target.checked ? [...value, r.name] : value.filter((x) => x !== r.name))}
              >
                {roleLabel(t, r.name)}{" "}
                <Typography.Text type="secondary">— {t(`users.roleDesc.${r.name}`, { defaultValue: r.description })}</Typography.Text>
              </Checkbox>
            ))}
          </Space>
        </fieldset>
      ))}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function CreateModal({ tenant, platformAdmin, roles, onClose, onDone }: { tenant: string; platformAdmin: boolean; roles: AssignableRole[]; onClose: () => void; onDone: (u: { username: string; temporary_password: string | null }) => void }) {
  const { t } = useTranslation("admin");
  const api = useApi();
  const { message } = App.useApp();
  const m = useMutation();
  const [f, setF] = useState<UserForm>({ username: "", first_name: "", last_name: "", email: "" });
  const [clearance, setClearance] = useState<Classification>("internal");
  const [locale, setLocale] = useState<UserLocale>("ar");
  const [picked, setPicked] = useState<string[]>([]);
  const touched = Object.values(f).some((v) => v.trim());
  const issues = userFormIssues(f, true);

  async function run() {
    const res = await m.run(() =>
      api.users.create({
        username: f.username.trim().toLowerCase(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        email: f.email.trim() || null,
        tenant: platformAdmin ? tenant : null,
        clearance,
        locale,
        roles: picked,
      }),
    );
    if (!res) return;
    void message.success(t("users.created", { name: res.username }));
    onDone(res);
  }

  const text = (k: keyof UserForm, id: string, dir: "ltr" | "auto", help?: string) => (
    <Form.Item label={t(`users.${k === "first_name" ? "firstName" : k === "last_name" ? "lastName" : k}`)} htmlFor={id} required={k !== "email"} help={help}>
      <Input
        id={id}
        dir={dir}
        value={f[k]}
        autoComplete="off"
        onChange={(e) => {
          const v = e.target.value;
          setF((c) => ({ ...c, [k]: v }));
        }}
      />
    </Form.Item>
  );

  return (
    <Modal open width={760} title={t("users.createTitle")} okText={t("users.create")} cancelText={t("cancel")} okButtonProps={{ disabled: issues.length > 0, loading: m.busy }} onOk={() => void run()} onCancel={onClose} destroyOnHidden>
      <Form layout="vertical" data-testid="user-create-form">
        {platformAdmin ? (
          <Typography.Paragraph>
            {t("users.tenant")}: <bdi>{tenantLabel(t, tenant)}</bdi>
          </Typography.Paragraph>
        ) : null}
        {text("username", "user-username", "ltr", t("users.usernameHelp"))}
        <Space wrap>
          {text("first_name", "user-first", "auto")}
          {text("last_name", "user-last", "auto")}
        </Space>
        {text("email", "user-email", "ltr")}
        <Space wrap>
          <Form.Item label={t("users.clearance")} htmlFor="user-clearance">
            <Select id="user-clearance" style={{ minInlineSize: 180 }} value={clearance} onChange={setClearance} options={CLASSIFICATIONS.map((c) => ({ value: c, label: t(`classification.${c}`) }))} />
          </Form.Item>
          <Form.Item label={t("users.locale")} htmlFor="user-locale">
            <Select id="user-locale" style={{ minInlineSize: 160 }} value={locale} onChange={setLocale} options={LOCALES.map((l) => ({ value: l, label: t(`users.localeName.${l}`) }))} />
          </Form.Item>
        </Space>
        <Typography.Title level={3} style={{ fontSize: 15 }}>
          {t("users.initialRoles")}
        </Typography.Title>
        <RoleEditor roles={roles} value={picked} onChange={setPicked} platformAdmin={platformAdmin} tenant={tenant} />
        <FormIssues ns="admin" issues={touched ? issues : []} error={m.error} />
      </Form>
    </Modal>
  );
}

function EditModal({ user, self, onClose, onDone }: { user: PlatformUser; self: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation("admin");
  const api = useApi();
  const { message } = App.useApp();
  const m = useMutation();
  const [f, setF] = useState<UserForm>({ username: user.username, first_name: user.first_name, last_name: user.last_name, email: user.email ?? "" });
  const [clearance, setClearance] = useState<Classification>(user.clearance);
  const [locale, setLocale] = useState<UserLocale>(user.locale ?? "ar");
  const [enabled, setEnabled] = useState(user.enabled);
  const issues = userFormIssues(f, false);
  const name = displayName(user);

  async function run() {
    const res = await m.run(() =>
      api.users.update(user.id, {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        email: f.email.trim() || null,
        ...(clearance !== user.clearance ? { clearance } : {}),
        locale,
        ...(enabled !== user.enabled ? { enabled } : {}),
      }),
    );
    if (!res) return;
    void message.success(t("users.saved", { name: res.username }));
    onDone();
  }

  const set = (k: keyof UserForm) => (e: { target: { value: string } }) => {
    const v = e.target.value;
    setF((c) => ({ ...c, [k]: v }));
  };

  return (
    <Modal open width={640} title={t("users.editTitle", { name })} okText={t("users.save")} cancelText={t("cancel")} okButtonProps={{ disabled: issues.length > 0, loading: m.busy }} onOk={() => void run()} onCancel={onClose} destroyOnHidden>
      <Form layout="vertical" data-testid="user-edit-form">
        <Typography.Paragraph>
          {t("users.username")}:{" "}
          <code className="ap-mono" dir="ltr">
            {user.username}
          </code>
        </Typography.Paragraph>
        <Space wrap>
          <Form.Item label={t("users.firstName")} htmlFor="edit-first" required>
            <Input id="edit-first" dir="auto" value={f.first_name} onChange={set("first_name")} />
          </Form.Item>
          <Form.Item label={t("users.lastName")} htmlFor="edit-last" required>
            <Input id="edit-last" dir="auto" value={f.last_name} onChange={set("last_name")} />
          </Form.Item>
        </Space>
        <Form.Item label={t("users.email")} htmlFor="edit-email">
          <Input id="edit-email" dir="ltr" value={f.email} onChange={set("email")} />
        </Form.Item>
        <Space wrap>
          <Form.Item label={t("users.clearance")} htmlFor="edit-clearance">
            <Select id="edit-clearance" style={{ minInlineSize: 180 }} value={clearance} onChange={setClearance} options={CLASSIFICATIONS.map((c) => ({ value: c, label: t(`classification.${c}`) }))} />
          </Form.Item>
          <Form.Item label={t("users.locale")} htmlFor="edit-locale">
            <Select id="edit-locale" style={{ minInlineSize: 160 }} value={locale} onChange={setLocale} options={LOCALES.map((l) => ({ value: l, label: t(`users.localeName.${l}`) }))} />
          </Form.Item>
        </Space>
        <Form.Item label={t("users.enabledLabel")} htmlFor="edit-enabled" help={self ? t("users.selfDisable") : undefined}>
          <Switch id="edit-enabled" checked={enabled} disabled={self} onChange={setEnabled} />
        </Form.Item>
        <FormIssues ns="admin" issues={issues} error={m.error} />
      </Form>
    </Modal>
  );
}

function RolesModal({ user, roles, platformAdmin, onClose, onDone }: { user: PlatformUser; roles: AssignableRole[]; platformAdmin: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation("admin");
  const api = useApi();
  const { message } = App.useApp();
  const m = useMutation();
  const assignable = new Set(roles.map((r) => r.name));
  const [picked, setPicked] = useState<string[]>(user.roles.filter((r) => assignable.has(r)));
  const others = user.roles.filter((r) => !assignable.has(r));
  const name = displayName(user);

  async function run() {
    const res = await m.run(() => api.users.setRoles(user.id, picked));
    if (!res) return;
    void message.success(t("users.rolesSaved", { name }));
    onDone();
  }

  return (
    <Modal open width={760} title={t("users.rolesTitle", { name })} okText={t("users.save")} cancelText={t("cancel")} okButtonProps={{ loading: m.busy }} onOk={() => void run()} onCancel={onClose} destroyOnHidden>
      <Form layout="vertical" data-testid="user-roles-form">
        <Typography.Paragraph type="secondary">{t("users.signOutNote")}</Typography.Paragraph>
        {others.length ? <Typography.Paragraph type="secondary">{t("users.otherRoles", { roles: others.map((r) => roleLabel(t, r)).join(", ") })}</Typography.Paragraph> : null}
        <RoleEditor roles={roles} value={picked} onChange={setPicked} platformAdmin={platformAdmin} tenant={user.tenant} />
        <FormIssues ns="admin" issues={[]} error={m.error} />
      </Form>
    </Modal>
  );
}

function ResetModal({ user, onClose, onDone }: { user: PlatformUser; onClose: () => void; onDone: (password: string) => void }) {
  const { t } = useTranslation("admin");
  const api = useApi();
  const m = useMutation();
  const name = displayName(user);

  async function run() {
    const res = await m.run(() => api.users.resetPassword(user.id));
    if (res?.temporary_password) onDone(res.temporary_password);
  }

  return (
    <Modal open title={t("users.resetTitle", { name })} okText={t("users.resetOk")} cancelText={t("cancel")} okButtonProps={{ danger: true, loading: m.busy }} onOk={() => void run()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph>{t("users.resetBody")}</Typography.Paragraph>
      <FormIssues ns="admin" issues={[]} error={m.error} />
    </Modal>
  );
}

/** The one and only display of a temporary password; it is not kept anywhere after closing. */
function PasswordModal({ name, value, onClose }: { name: string; value: string; onClose: () => void }) {
  const { t } = useTranslation("admin");
  const { message } = App.useApp();

  async function copy() {
    try {
      await navigator.clipboard?.writeText(value);
      void message.success(t("users.copied"));
    } catch {
      /* clipboard unavailable: the text stays selectable */
    }
  }

  return (
    <Modal
      open
      title={t("users.passwordTitle", { name })}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Button type="primary" onClick={onClose}>
          {t("users.done")}
        </Button>
      }
    >
      <Alert type="warning" showIcon message={t("users.passwordOnce")} style={{ marginBlockEnd: 12 }} />
      <Space.Compact style={{ inlineSize: "100%" }}>
        <Input readOnly dir="ltr" className="ap-mono" aria-label={t("users.passwordTitle", { name })} value={value} data-testid="temporary-password" onFocus={(e) => e.target.select()} />
        <Button onClick={() => void copy()}>{t("users.copy")}</Button>
      </Space.Compact>
    </Modal>
  );
}
